import React from "react";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { fmtDashboardName } from "../../utils/format.util";
import { Box, createStyles, Menu } from "@mantine/core";
import { IconV3, RpiLabelText } from "@rpi/openapi-core";
import { tokens } from "@blurb/codex-react";

const useStyles = createStyles(() => ({
  navBarAccountButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: tokens.spacing[3],
    gap: tokens.spacing[2],
    width: 48,
    height: 48,
    borderRadius: tokens.radius.sm,
    textDecoration: "none",
    flexDirection: "row",
    color: tokens.color.semantic.text.bold,
    backgroundColor: "transparent",
    border: `1px solid ${tokens.color.primitive.light.gray[200]}`,
    cursor: "pointer",
    transition: "width 0.3s ease, padding 0.3s ease",
    overflow: "hidden",
    "&:hover": {
      backgroundColor: tokens.color.semantic.bg.brandSubtlest,
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.color.semantic.border.focus}`,
      outlineOffset: "2px",
    },
    "nav:hover &, nav:has(:focus-visible) &, nav:has([aria-expanded='true']) &": {
      width: "100%",
    },
  },
  navBarAccountAvatar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.color.primitive.light.calm[700],
    color: tokens.color.primitive.base.white,
    flexShrink: 0,
  },
  navBarAccountText: {
    whiteSpace: "nowrap",
    opacity: 0,
    transition: "opacity 0.2s ease",
    "nav:hover &, nav:has(:focus-visible) &, nav:has([aria-expanded='true']) &": {
      opacity: 1,
    },
  },
  navBarAccountMenu: {
    borderRadius: tokens.radius.lg,
    background: tokens.color.semantic.bg.surfaceRaised,
    padding: tokens.spacing[2],
    boxShadow: "0 4px 12px 1px rgba(0, 0, 0, 0.10)",
  },
  navBarAccountMenuItem: {
    fontSize: tokens.font.size.base,
    gap: tokens.spacing[2],
    padding: tokens.spacing[2],
    borderRadius: tokens.radius.sm,
    "&:hover": {
      backgroundColor: tokens.color.semantic.bg.brandSubtlest,
    },
  },
}));

export const NavBarAccountButton: React.FC<React.HTMLAttributes<HTMLButtonElement>> = () => {
  const { classes } = useStyles();
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const [opened, setOpened] = React.useState(false);

  const displayName = fmtDashboardName(user.dashboardUser);

  const getInitials = () => {
    return displayName?.charAt(0).toUpperCase() || "U";
  };

  const handleMenuItemClick = (callback: () => void) => {
    callback();
    setOpened(false);
  };

  return (
    <Menu
      width={220}
      position="right-start"
      offset={8}
      opened={opened}
      onChange={setOpened}
      withinPortal={true}
      classNames={{
        dropdown: classes.navBarAccountMenu,
        item: classes.navBarAccountMenuItem,
      }}
    >
      <Menu.Target>
        <Box
          className={classes.navBarAccountButton}
          component="button"
          aria-label={`Profile menu for ${displayName}`}
          aria-expanded={opened}
          aria-haspopup="menu"
          tabIndex={0}
        >
          <Box className={classes.navBarAccountAvatar} aria-hidden="true">
            <RpiLabelText>{getInitials()}</RpiLabelText>
          </Box>
          <span className={classes.navBarAccountText}>
            <RpiLabelText>{displayName}</RpiLabelText>
          </span>
        </Box>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconV3 icon="help" size={20} aria-hidden="true" />}
          onClick={() => handleMenuItemClick(() => navigateRoute(["settings"]))}
          role="menuitem"
        >
          Help
        </Menu.Item>
        <Menu.Item
          icon={<IconV3 icon="settings" size={20} aria-hidden="true" />}
          onClick={() => handleMenuItemClick(() => navigateRoute(["settings"]))}
          role="menuitem"
        >
          Account Settings
        </Menu.Item>
        <Menu.Divider role="separator" />
        <Menu.Item
          icon={<IconV3 icon="logout" size={20} aria-hidden="true" />}
          onClick={() => handleMenuItemClick(() => user.signOut())}
          role="menuitem"
        >
          Sign Out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
